import { useNavigate } from 'react-router-dom';
import {
  PullToRefresh,
  Grid,
  Badge,
  Skeleton,
  Toast,
  Avatar,
} from 'antd-mobile';
import {
  FileOutline,
  EditSFill,
  FireFill,
  ExclamationCircleFill,
  CheckCircleFill,
} from 'antd-mobile-icons';
import { useQuery } from '@tanstack/react-query';
import { statisticsApi } from '@/api/tickets';
import { useAuthStore } from '@/stores/authStore';
import { getRoleText } from '@/utils/formatters';
import type { TicketStatistics } from '@shared/types';

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const {
    data: stats,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['statistics'],
    queryFn: async () => {
      const res = await statisticsApi.get();
      return res.data.data;
    },
    staleTime: 1000 * 60,
  });

  const handleRefresh = async () => {
    await refetch();
    Toast.show({ icon: 'success', content: '刷新成功' });
  };

  const getProgressColor = (rate: number) => {
    if (rate < 50) return '#ff4d4f';
    if (rate < 80) return '#faad14';
    return '#52c41a';
  };

  return (
    <div className="home-page">
      <PullToRefresh onRefresh={handleRefresh}>
        {/* 顶部用户栏 */}
        <div className="home-header">
          <div className="home-header-content">
            <div className="user-info">
              <img
                src="/avatar.png"
                alt="avatar"
                className="user-avatar"
                style={{
                  width: 38, height: 38, borderRadius: '50%',
                  objectFit: 'cover', display: 'block',
                  border: '2px solid #2E5BFF30',
                }}
              />
              <div className="user-meta">
                <div className="user-name">
                  {user?.real_name || '访客'}
                  <span className="user-role">{getRoleText(user?.role || '')}</span>
                </div>
                <div className="user-greeting">
                  {getGreeting()}，今日工单管理就绪
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 统计卡片 — 横向滚动 */}
        <div className="stats-section">
          <div className="stats-scroll">
            {isLoading ? (
              <>
                <Skeleton animated className="stat-skeleton" />
                <Skeleton animated className="stat-skeleton" />
                <Skeleton animated className="stat-skeleton" />
              </>
            ) : (
              <>
                <StatCard
                  title="红警工单"
                  value={stats?.red_count || 0}
                  icon={<FireFill />}
                  bg="linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)"
                  onClick={() => navigate('/tickets?level=red')}
                />
                <StatCard
                  title="黄警工单"
                  value={stats?.yellow_count || 0}
                  icon={<ExclamationCircleFill />}
                  bg="linear-gradient(135deg, #faad14 0%, #ffc53d 100%)"
                  onClick={() => navigate('/tickets?level=yellow')}
                />
                <StatCard
                  title="整改率"
                  value={`${stats?.rectify_rate || 0}%`}
                  icon={<CheckCircleFill />}
                  bg="linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%)"
                  suffix
                />
              </>
            )}
          </div>
        </div>

        {/* 今日整改进度 */}
        <div className="progress-section">
          <div className="progress-card">
            <div className="progress-header">
              <span className="progress-title">今日整改进度</span>
              <span className="progress-value" style={{ color: getProgressColor(stats?.rectify_rate || 0) }}>
                {stats?.today_rectified || 0} / {stats?.today_total || 0}
              </span>
            </div>
            <div className="progress-bar-bg">
              <div
                className="progress-bar-fill"
                style={{
                  width: `${stats?.rectify_rate || 0}%`,
                  background: getProgressColor(stats?.rectify_rate || 0),
                }}
              />
            </div>
            <div className="progress-desc">
              {stats?.today_total === 0
                ? '今日暂无工单'
                : `已完成 ${stats?.rectify_rate || 0}% 的整改任务`}
            </div>
          </div>
        </div>

        {/* 快捷操作 */}
        <div className="quick-actions-section">
          <h3 className="section-title">快捷操作</h3>
          <Grid columns={2} gap={12}>
            <Grid.Item onClick={() => navigate('/tickets/new')}>
              <div className="action-card action-primary">
                <div className="action-icon">📋</div>
                <div className="action-name">上报问题</div>
              </div>
            </Grid.Item>
            <Grid.Item onClick={() => navigate('/tickets?status=pending')}>
              <div className="action-card">
                <div className="action-icon">⏰</div>
                <div className="action-name">待办工单</div>
                {stats?.pending_count ? (
                  <Badge
                    content={stats.pending_count > 99 ? '99+' : stats.pending_count}
                    className="action-badge"
                  />
                ) : null}
              </div>
            </Grid.Item>
            <Grid.Item onClick={() => navigate('/tickets')}>
              <div className="action-card">
                <div className="action-icon">📑</div>
                <div className="action-name">全部工单</div>
              </div>
            </Grid.Item>
            <Grid.Item onClick={() => navigate('/profile')}>
              <div className="action-card">
                <div className="action-icon">👤</div>
                <div className="action-name">个人中心</div>
              </div>
            </Grid.Item>
          </Grid>
        </div>

        {/* 工单概览 */}
        <div className="recent-section">
          <h3 className="section-title">工单概览</h3>
          <div className="overview-grid">
            <OverviewItem label="待整改" value={stats?.pending_count || 0} color="#ff4d4f" />
            <OverviewItem label="已整改" value={stats?.rectified_count || 0} color="#2E5BFF" />
            <OverviewItem label="已验收" value={stats?.verified_count || 0} color="#52c41a" />
            <OverviewItem label="已关闭" value={stats?.closed_count || 0} color="#9CA3AF" />
          </div>
        </div>

        <div className="home-footer">
          <p>现场智管 v1.0 · 数据实时同步</p>
        </div>
      </PullToRefresh>

      <style>{`
        .home-page {
          min-height: 100vh;
          background: #F5F7FA;
          padding-bottom: calc(16px + env(safe-area-inset-bottom));
        }

        .home-header {
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          padding: 12px 16px 22px;
          position: relative;
        }

        .home-header::after {
          content: '';
          position: absolute;
          bottom: -20px;
          left: 0;
          right: 0;
          height: 40px;
          background: #F5F7FA;
          border-radius: 24px 24px 0 0;
        }

        .home-header-content {
          position: relative;
          z-index: 1;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-avatar {
          flex-shrink: 0;
          background: rgba(255, 255, 255, 0.2) !important;
          color: white;
        }

        .user-meta {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          color: white;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .user-role {
          font-size: 11px;
          font-weight: 500;
          padding: 2px 8px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          color: white;
        }

        .user-greeting {
          color: rgba(255, 255, 255, 0.85);
          font-size: 12px;
          margin-top: 1px;
        }

        .stats-section {
          padding: 0 12px;
          margin-top: -10px;
          position: relative;
          z-index: 2;
        }

        .stats-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 8px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .stats-scroll::-webkit-scrollbar {
          display: none;
        }

        .stat-skeleton {
          width: 145px;
          height: 88px;
          border-radius: 14px;
          flex-shrink: 0;
        }

        .stat-card {
          min-width: 145px;
          padding: 12px;
          border-radius: 14px;
          color: white;
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
          cursor: pointer;
          transition: transform 0.2s ease;
          user-select: none;
        }

        .stat-card:active {
          transform: scale(0.97);
        }

        .stat-card-bg {
          position: absolute;
          top: -20px;
          right: -20px;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
        }

        .stat-card-icon {
          font-size: 20px;
          margin-bottom: 8px;
          opacity: 0.9;
        }

        .stat-card-title {
          font-size: 12px;
          opacity: 0.85;
          margin-bottom: 2px;
        }

        .stat-card-value {
          font-size: 22px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.5px;
        }

        .progress-section {
          padding: 12px;
        }

        .progress-card {
          background: white;
          border-radius: 16px;
          padding: 12px 14px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .progress-title {
          font-size: 14px;
          font-weight: 600;
          color: #1F2433;
        }

        .progress-value {
          font-size: 14px;
          font-weight: 600;
        }

        .progress-bar-bg {
          height: 6px;
          background: #F0F2F5;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 4px;
        }

        .progress-bar-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.6s ease;
        }

        .progress-desc {
          font-size: 12px;
          color: #9CA3AF;
        }

        .quick-actions-section {
          padding: 0 12px 12px;
        }

        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #1F2433;
          margin: 0 0 8px 2px;
        }

        .action-card {
          background: white;
          border-radius: 14px;
          padding: 12px 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          position: relative;
          box-shadow: 0 2px 12px rgba(30, 63, 204, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
          border: 1px solid #E8ECF3;
          cursor: pointer;
          transition: all 0.25s ease;
          user-select: none;
        }

        .action-card:active {
          transform: scale(0.97);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        }

        .action-card.action-primary {
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          color: white;
        }

        .action-card.action-primary .action-desc {
          color: rgba(255, 255, 255, 0.75);
        }

        .action-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: rgba(22, 119, 255, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }

        .action-card.action-primary .action-icon {
          background: rgba(255, 255, 255, 0.15);
        }

        .action-text {
          flex: 1;
          min-width: 0;
        }

        .action-name {
          font-size: 13px;
          font-weight: 600;
          color: #1F2433;
          text-align: center;
          line-height: 1.2;
        }

        .action-card.action-primary .action-name {
          color: white;
        }

        .action-desc {
          font-size: 12px;
          color: #9CA3AF;
        }

        .action-badge {
          position: absolute;
          top: 8px;
          right: 8px;
        }

        .recent-section {
          padding: 0 12px 12px;
        }

        .overview-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
        }

        .overview-item {
          background: white;
          border-radius: 12px;
          padding: 10px 6px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .overview-value {
          font-size: 18px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          margin-bottom: 2px;
        }

        .overview-label {
          font-size: 12px;
          color: #9CA3AF;
        }

        .home-footer {
          text-align: center;
          padding: 16px 12px 12px;
        }

        .home-footer p {
          font-size: 12px;
          color: #ccc;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

/* 统计卡片子组件 */
function StatCard({
  title,
  value,
  icon,
  bg,
  suffix = false,
  onClick,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  bg: string;
  suffix?: boolean;
  onClick?: () => void;
}) {
  return (
    <div className="stat-card" style={{ background: bg }} onClick={onClick}>
      <div className="stat-card-bg" />
      <div className="stat-card-icon">{icon}</div>
      <div className="stat-card-title">{title}</div>
      <div className="stat-card-value">{value}{suffix ? '' : ''}</div>
    </div>
  );
}

/* 概览项子组件 */
function OverviewItem({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="overview-item">
      <div className="overview-value" style={{ color }}>{value}</div>
      <div className="overview-label">{label}</div>
    </div>
  );
}

/* 问候语 */
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 6) return '凌晨好';
  if (hour < 9) return '早上好';
  if (hour < 12) return '上午好';
  if (hour < 14) return '中午好';
  if (hour < 18) return '下午好';
  return '晚上好';
}